'use client';

import { datadogRum } from '@datadog/browser-rum';
import { ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Auth } from 'aws-amplify';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import '@i18n';

import { ErrorBoundary } from '@components/ErrorBoundary';
import { LabelPreview } from '@components/LabelPreview/LabelPreview';
import { Loader } from '@components/Loader';
import NetworkStatusModal from '@components/NetworkStatus/NetworkStatusPopup';
import { ReceiptPreview } from '@components/ReceiptPreview';

import {
  SysRole,
  authenticateUser,
  deleteValueFromLocalStorage,
  getValueFromLocalStorage,
  muiTheme,
  requestTokenRefreshInterceptor,
  setValueInLocalStorage,
} from '@utils';
import { getEnvVar } from '@utils/environmentVariables';
import { initDataDog } from '@utils/initDataDog';

import {
  BranchConfigV2,
  BranchMenusV2,
  useGetBranchConfig,
  useGetBranchConfigV2,
  useGetBranchDetails,
  useGetGlobal,
} from '@api/generator';
import { AXIOS_INSTANCE } from '@api/generator/mutator/customInstance';
import { AXIOS_INSTANCE_NO_FADCODE } from '@api/generatorNoFadcode/mutator/customInstanceNoFadcode';

import { authConfig } from '@config';
import { useResponse401Interceptor, useRequestFadcodeInterceptor } from 'hooks/interceptors';
import { useAutoLogout } from 'hooks/useAutoLogout';
import { useClearAppData } from 'hooks/useClearAppData';

import { CT_URL_LINK, useCounterTerminal } from '@hooks/useCounterTerminal';
import useNetworkStatus from '@hooks/useNetworkStatus';
import { useTypedSearchParams } from '@hooks/useTypedSearchParams';

import { persistor, store, useAppDispatch, useAppSelector } from '@store';
import { BranchLimits, setBranchInfo } from '@store/branchInfo.slice';
import { setCounterData } from '@store/counter.slice';
import { setUser } from '@store/user.slice';

import { deviceServerSimulated } from '@service/printer';

import { constructRouteWithParams, nunitoSans, SCREENS } from '@constants';

import { Header } from './common/Header';
import styles from './layout.module.css';
import { TitleContextProvider } from './PageTitleContext';

import './globals.scss';

initDataDog();
Auth.configure(authConfig);

type SK = keyof typeof SCREENS;

export interface CtToBoQueryParams {
  targetRoute: SK;
  fadcode: string | undefined;
  currentAccountingLocationID: string | undefined;
  navigatedFromCT: string | undefined;
}

function Content({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const { isFromCT, isElectron, isFromCtSimulator } = useCounterTerminal();
  useAutoLogout(CT_URL_LINK, isElectron, isFromCtSimulator);
  const { clearAppData } = useClearAppData();
  const { response401Interceptor } = useResponse401Interceptor();
  const { requestFadcodeInterceptor } = useRequestFadcodeInterceptor();
  const { fadcode } = useAppSelector((state) => state.branchInfo);
  const { smartID } = useAppSelector((state) => state.user);
  const [sysRole, setSysRole] = useState<SysRole[] | null>(null);
  const [isLoading, setIsLoading] = useState({
    interceptors: true,
    branchInfo: false, // Not loading by default as we won't be fetching this before the user has selected a fadcode
  });
  const isOnline = useNetworkStatus();
  const [showNetworkStatus, setShowNetworkStatus] = useState(false);
  const { navigatedFromCT, fadcode: fadcodeQueryParam } = useTypedSearchParams('BranchOverview');
  const queryParams = useTypedSearchParams('CashCount');
  const { currentAccountingLocationID } = queryParams;
  const router = useRouter();

  // Set up Axios interceptors
  useEffect(() => {
    // Interceptors for requests that require fadcode
    AXIOS_INSTANCE.interceptors.response.use(...response401Interceptor);
    AXIOS_INSTANCE.interceptors.request.use(...requestTokenRefreshInterceptor);
    AXIOS_INSTANCE.interceptors.request.use(...requestFadcodeInterceptor);

    // Interceptors for requests that do not require fadcode
    AXIOS_INSTANCE_NO_FADCODE.interceptors.response.use(...response401Interceptor);
    AXIOS_INSTANCE_NO_FADCODE.interceptors.request.use(...requestTokenRefreshInterceptor);

    setIsLoading((prev) => ({
      ...prev,
      interceptors: false,
    }));
  }, [requestFadcodeInterceptor, response401Interceptor]);

  // Authenticate user and set user info in redux store
  useEffect(() => {
    const startAuthFlow = async () => {
      if (fadcodeQueryParam) {
        // Temporarily save CT -> BO query params so they're not lost while authenticating
        setValueInLocalStorage<CtToBoQueryParams>('navigationParams', {
          fadcode: fadcodeQueryParam,
          targetRoute: Object.keys(SCREENS).find(
            (key) => SCREENS[key as SK] === window.location.pathname,
          ) as SK,
          navigatedFromCT,
          ...queryParams,
        });
      }

      const authData = await authenticateUser(clearAppData);

      if (authData) {
        setSysRole(authData.sysRole);

        dispatch(
          setUser({
            username: authData?.username ?? '',
            smartID: authData?.smartId ?? '',
          }),
        );
      }
    };

    startAuthFlow();
  }, [clearAppData, currentAccountingLocationID, dispatch, fadcodeQueryParam, navigatedFromCT]);

  // Set fadcode if there is only one sysRole, or redirect to branch selection screen
  // if there are multiple sysRoles
  useEffect(() => {
    if (!sysRole) {
      return;
    }

    const navigationParams = getValueFromLocalStorage<CtToBoQueryParams>('navigationParams');

    if (navigationParams) {
      deleteValueFromLocalStorage('navigationParams');
      dispatch(
        setCounterData({
          navigatedFromCT: true,
        }),
      );
      if (navigationParams.fadcode) {
        dispatch(setBranchInfo({ fadcode: navigationParams.fadcode }));
      }
      if (navigationParams.targetRoute) {
        router.replace(
          constructRouteWithParams(navigationParams.targetRoute, {
            ...navigationParams,
            fadcode: navigationParams.fadcode,
          }),
        );
      }
      return;
    }

    if (fadcode) {
      return;
    }

    if (sysRole.length === 1) {
      // If there is only one sysRole we set the fadcode in the redux store.
      const fadcodeFromAuth = sysRole[0].fadCode;
      dispatch(setBranchInfo({ fadcode: fadcodeFromAuth }));
      return;
    }

    if (window.location.pathname !== SCREENS.BranchSelection) {
      // If there are multiple sysRoles we redirect the user to the branch selection screen
      router.replace(SCREENS.BranchSelection);
    }
  }, [sysRole, dispatch, fadcode, router]);

  const { data: branchDetails, isLoading: isBranchDetailsLoading } = useGetBranchDetails({
    query: { enabled: !!fadcode },
  });
  const { data: branchTypeConfig, isLoading: isBranchTypeConfigLoading } = useGetBranchConfigV2(
    branchDetails?.branchType as 'DMB' | 'SP' | 'IND', // TODO: Remove "as" when branchDetails is properly typed
    { query: { enabled: !!fadcode && !!branchDetails?.branchType } },
  );

  const { data: menuList, isLoading: isMenuListLoading } = useGetBranchConfig('menu-v2', {
    query: { enabled: !!fadcode },
  }) satisfies {
    data: BranchMenusV2 | undefined;
  };

  const { data: encodedBranchLimits, isLoading: isEncodedBranchLimitsLoading } = useGetGlobal(
    'metadata',
    { query: { enabled: !!fadcode } },
  );

  // Set branch info in redux store
  useEffect(() => {
    // These requests won't be attempted until a fadcode has been selected. Once a fadcode has been
    // selected and these are loading, trigger the branchInfo loading state until the branchInfo has been
    // calculated below
    if (isBranchDetailsLoading || isBranchTypeConfigLoading || isEncodedBranchLimitsLoading) {
      setIsLoading((prev) => ({
        ...prev,
        branchInfo: true,
      }));
    }

    if (!branchDetails || !branchTypeConfig || !encodedBranchLimits) return;

    const branchLimits: BranchLimits = JSON.parse(atob(encodedBranchLimits.message));
    const branchAddress = `${branchDetails?.addressLine1} ${branchDetails?.addressLine2} ${branchDetails?.addressLine3} ${branchDetails?.addressLine4} ${branchDetails?.addressLine5}`;

    const { addressLine1, addressLine2, addressLine3, addressLine4, addressLine5, branchPostcode } =
      branchDetails;

    dispatch(
      setBranchInfo({
        branchName: branchDetails.branchName ?? '',
        branchAddress,
        isRoyalMailsBranch: false, // TODO: Remove hard-coded value
        branchTypeConfig: {
          ...branchTypeConfig,
          branchType: branchDetails?.branchType, // TODO: Update End point?
        } as BranchConfigV2,
        branchLimits,
        branchAddressFull: {
          addressLine1,
          addressLine2,
          addressLine3,
          addressLine4,
          addressLine5,
          branchPostcode,
        },
      }),
    );

    // branchInfo has been set in redux state, disable the loading state and render the children
    setIsLoading((prev) => ({ ...prev, branchInfo: false }));
  }, [
    branchDetails,
    branchTypeConfig,
    encodedBranchLimits,
    dispatch,
    isBranchDetailsLoading,
    isBranchTypeConfigLoading,
    isEncodedBranchLimitsLoading,
  ]);

  // Set user info in DataDogRum
  useEffect(() => {
    if (!smartID || !fadcode) return;

    datadogRum.setUser({
      id: smartID,
      branch: fadcode,
      deviceId: window.electronAPI?.getSerialNumber() || undefined,
    });
  }, [smartID, fadcode]);

  useEffect(() => {
    if (!isOnline) {
      setShowNetworkStatus(true);
    }
  }, [isOnline, showNetworkStatus]);

  const handleCloseNetworkModal = () => {
    setShowNetworkStatus(false);
  };

  if (Object.values(isLoading).some((value) => value)) {
    return <Loader testId="layoutLoading" />;
  }

  return (
    <>
      <Header
        menuList={menuList}
        isMenuListLoading={isMenuListLoading}
        fadcode={fadcode}
        isFromCT={isFromCT}
      />
      <main>{children}</main>
      {deviceServerSimulated && <ReceiptPreview />}
      {deviceServerSimulated && <LabelPreview />}
      {showNetworkStatus && (
        <NetworkStatusModal isOnline={isOnline} close={handleCloseNetworkModal} />
      )}
    </>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const launchDarklyConfig = {
  clientSideID: getEnvVar('NEXT_PUBLIC_LAUNCH_DARKLY_CLIENT_KEY') ?? '',
  context: {
    kind: 'anonymous',
    key: 'anonymous-bo-user', // this can be replaced with a unique key, but for now its not needed in BO
    anonymous: true,
  },
  options: {
    application: {
      id: 'back-office',
    },
  },
};

export function AppWrapper({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <body className={nunitoSans.className}>
        <div className={styles.container}>
          <QueryClientProvider client={queryClient}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <ThemeProvider theme={muiTheme}>
                <LDProvider {...launchDarklyConfig}>
                  <TitleContextProvider>
                    <Provider store={store}>
                      <PersistGate
                        loading={<Loader testId="reduxPersistStoreLoader" />}
                        persistor={persistor}>
                        <ErrorBoundary>
                          {/* Content is a separate component so it can access the React Query Provider */}
                          <Content>{children}</Content>
                        </ErrorBoundary>
                      </PersistGate>
                    </Provider>
                  </TitleContextProvider>
                </LDProvider>
              </ThemeProvider>
            </LocalizationProvider>
          </QueryClientProvider>
        </div>
      </body>
    </html>
  );
}
